'use client';

import type { TopNavigationIconButtonType, TopNavigationRightButtonProps } from '@yanolja-rnd/nol-design-system';
import type { FC } from 'react';
import type { Category } from '@/app/(pages)/sub-home/[category]/types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useRef, useEffect, useState } from 'react';
import { CountBadge, TopNavigation } from '@yanolja-rnd/nol-design-system';
import { IcArrowback, IcCart, IcSearch } from '@yanolja-rnd/nol-icons';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { throttle } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';
import { clientLogStandardized } from '@weberus/client-tracker';
import { useAtomValue } from 'jotai';
import config from '@/conf';
import { cn } from '@/app/_shared/utils/tw-cn';
import { queries } from '@/app/_shared/queries';
import { forceBackFromExternal } from '@/app/_shared/utils/navigation';
import { SUB_HOME_TYPE_TO_SEARCH_URL } from '@/app/(pages)/sub-home/[category]/constants';
import { deviceAtom } from '@/app/_shared/store/device';

interface HeaderProps {
  category: Category;
  title: string;
  pageNameLog: HomeLogPageNameValue;
}

const Header: FC<HeaderProps> = ({ category, title, pageNameLog }) => {
  const stickyRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const { data } = useQuery(queries.journey.cartCount());
  const cartCount = data?.data?.count;

  const { deviceType } = useAtomValue(deviceAtom);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (stickyRef.current) {
        setIsSticky(window.scrollY > 0);
      }
    }, 100);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const leftButton: TopNavigationIconButtonType = {
    'aria-label': 'Back',
    icon: <IcArrowback />,
    onClick: () => {
      clientLogStandardized('click', pageNameLog, { object: 'backwardBtn', objectContainer: 'header' });
      forceBackFromExternal('/');
    },
  };

  const rightButtons: TopNavigationRightButtonProps[] = [
    {
      type: 'Icon',
      button: {
        'aria-label': 'Search',
        icon: <IcSearch />,
        onClick: () => {
          clientLogStandardized('click', pageNameLog, { object: 'searchBtn', objectContainer: 'header' });
          location.href = SUB_HOME_TYPE_TO_SEARCH_URL[category];
        },
      },
    },
    {
      type: 'Icon',
      button: {
        'aria-label': 'Cart',
        icon: <IcCart />,
        onClick: () => {
          clientLogStandardized('click', pageNameLog, { object: 'cartBtn', objectContainer: 'header' });
          const cartLink = `${config.service.platformSite.origin}/cart`;
          if (yaNative.isEnabled) {
            yaNative.wv({ url: cartLink });
            return;
          }
          location.href = cartLink;
        },
      },
      badge: cartCount ? (
        <CountBadge className="absolute right-3 top-4 pc:right-7 pc:top-7">{cartCount}</CountBadge>
      ) : undefined,
    },
  ];

  return (
    <div
      ref={stickyRef}
      className={cn(
        'fixed top-0 w-full max-w-mobile-size bg-static-white z-middle pc:max-w-[unset] pc:border-b-[1px] pc:border-b-line-neutral-weak1',
        {
          'shadow-[0px_6px_8px_-6px_rgba(0,0,0,0.2)]': isSticky,
        },
      )}>
      <div className="mx-auto pc:max-w-pc-size pc:shadow-none">
        <TopNavigation leftButton={leftButton} rightButtons={rightButtons} title={title} deviceType={deviceType} />
      </div>
    </div>
  );
};

export default Header;
